import { Text, Ticker } from 'pixi.js';
import type { AnyPlayActResult, AnyPlayView, PlayOffer } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { destination, gameStart, prizeWords, reduceGame, type GameEvent, type GameView } from '../../logic/minigames/core';
import { GAME_SOUNDS } from '../../logic/minigames/sounds';
import { ROAD_SOUNDS } from '../../logic/road/sounds';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton, layoutButtonRow } from '../board/BrassButton';
import { COLOURS, displayStyle, uiStyle } from '../board/ui';
import { Explainer } from './pieces';
import type { MiniGameDeps, MiniGameEnd, MiniGameScreen, MiniGameSnapshot, PlayApi } from './types';

/**
 * A mini-game played one act at a time against the server (smash, match,
 * scratch, wheel), on a board: a line under the title, the game's pieces,
 * then what it paid and a button. {@link reduceGame} keeps one act in
 * flight; each game draws its pieces and says which taps count.
 *
 * A game that paid nothing says so plainly and its button reads DONE; one
 * that paid shows the amount and COLLECT, which flies the coins into the
 * wallet (or, for a lamp's game, leaves them in the run's pot). A game can
 * also end in a loss that still pays a little (a miss at cups, a bomb in
 * the dig): that says so in red, shows the small amount plainly, gets no
 * confetti or fanfare, and ends with DONE; the coins still fly home.
 */
/** Room kept above and below the board: the top bar (with the title
 *  plaque clear of it) and the board's own padding. */
const BOARD_CLEARANCE = 184;

export abstract class TurnGameDialog<O extends PlayOffer, S, R> extends BoardDialog implements MiniGameScreen {
  protected readonly lead: Text;
  protected readonly amount: Text;
  protected readonly note: Text;
  protected readonly error: Text;
  /** The left button, when a game has one (Scratch them all). */
  protected readonly side: BrassButton;
  protected readonly main: BrassButton;
  protected offer: O | null = null;
  protected game: GameView<S, R> = gameStart<S, R>(false);
  private api: PlayApi | null = null;
  private done: ((end: MiniGameEnd) => void) | null = null;
  private closing = false;
  private laidOutWidth = 0;
  private laidOutHeight = 0;
  /** The screen's height, for fitting the pieces under the top bar. */
  private screenH = 800;
  /** One-tap explanations (the counters, a ×2, a tile's prize). */
  private readonly explainer: Explainer;
  protected clock = 0;
  /** While true, an answer that ended the game isn't shown yet (the wheel
   *  is still turning); the game calls {@link reveal} when it is. */
  protected settling = false;
  private readonly tick = (ticker: Ticker) => {
    this.clock += ticker.deltaMS / 1000;
    this.animate(ticker.deltaMS);
  };

  constructor(
    protected readonly deps: MiniGameDeps,
    readonly kind: string,
    title: string,
    maxWidth = 420,
  ) {
    super({ title, floor: deps.floor(), maxWidth });
    this.lead = new Text({ text: '', style: { ...uiStyle(17), align: 'center', wordWrap: true } });
    this.lead.anchor.set(0.5, 0);
    this.amount = new Text({ text: '', style: displayStyle(30) });
    this.amount.anchor.set(0.5, 0);
    this.note = new Text({ text: '', style: { ...uiStyle(14, 0xf0dcb4, '500'), align: 'center' } });
    this.note.anchor.set(0.5, 0);
    this.error = new Text({ text: '', style: { ...uiStyle(14, COLOURS.loss, '600'), align: 'center', wordWrap: true } });
    this.error.anchor.set(0.5, 0);
    this.side = new BrassButton({ label: '', kind: 'wood', size: 16, onTap: () => this.onSide() });
    this.main = new BrassButton({ label: 'COLLECT', kind: 'gold', onTap: () => void this.onMain() });
    this.explainer = new Explainer(() => this.deps.floor());
    this.content.addChild(this.lead, this.amount, this.note, this.error, this.side, this.main);
  }

  /** Add the pieces' own sprites first, then call this so the explanation
   *  bubble draws over them. */
  protected keepExplainerOnTop(): void {
    this.content.addChild(this.explainer);
  }

  play(offer: PlayOffer, api: PlayApi): Promise<MiniGameEnd> {
    this.offer = offer as O;
    this.api = api;
    this.closing = false;
    this.game = gameStart<S, R>(offer.creditTo === 'parent');
    this.setFloor(this.deps.floor());
    this.explainer.hide();
    this.begin(this.offer);
    this.keepExplainerOnTop();
    this.render();
    Ticker.shared.add(this.tick);
    void this.open();
    return new Promise((resolve) => (this.done = resolve));
  }

  override layout(viewWidth: number, viewHeight: number): void {
    this.screenH = viewHeight;
    super.layout(viewWidth, viewHeight);
    if (this.offer && (this.contentWidth !== this.laidOutWidth || viewHeight !== this.laidOutHeight)) this.render();
  }

  get snapshot(): MiniGameSnapshot {
    return {
      kind: this.kind,
      open: this.visible,
      title: this.titleText,
      lead: this.lead.text,
      pieces: this.pieceLabels(),
      result: this.amount.visible ? this.amount.text : '',
      note: this.note.visible ? this.note.text : '',
      buttons: [this.side, this.main].filter((b) => b.visible).map((b) => b.labelText),
      explained: this.explainer.text,
      ...(this.counterValues() ? { counters: this.counterValues()! } : {}),
    };
  }

  press(label: string): boolean {
    const button = [this.side, this.main].find((b) => b.visible && b.labelText.startsWith(label));
    button?.onTap?.();
    return !!button;
  }

  tap(index: number): boolean {
    if (!this.visible) return false;
    this.onPiece(index);
    return true;
  }

  // ---- for each game ----------------------------------------------------

  /** A new play: build the pieces for it. */
  protected abstract begin(offer: O): void;
  /** Draw the pieces (and any counters under them) from `y` across
   *  `width`, in at most `maxHeight`; returns the y below them. */
  protected abstract renderPieces(y: number, width: number, maxHeight: number): number;
  /** A piece was tapped. */
  protected abstract onPiece(index: number): void;
  /** The line under the title. */
  protected abstract leadText(): string;
  /** Each piece's label, for tests. */
  protected abstract pieceLabels(): string[];
  /** The server answered an act (before the board is redrawn). */
  protected onAnswered(_prev: GameView<S, R>, _next: GameView<S, R>): void {}
  /** The left button during play, or null for none. */
  protected sideLabel(): string | null {
    return null;
  }
  protected onSideTap(): void {}
  /** The gold button during play (SPIN), or null for none. */
  protected actionLabel(): string | null {
    return null;
  }
  protected onActionTap(): void {}
  /** Per frame, while the game is open. */
  protected animate(_deltaMs: number): void {}
  /** Whether what the game paid is a win. A game that can end in a loss
   *  that still pays a little says no then. */
  protected wonPrize(): boolean {
    return true;
  }
  /** Keep the button row's room while no button shows (a timed round),
   *  so the board doesn't jump when its button goes. */
  protected keepButtonRoom(): boolean {
    return false;
  }
  /** Lines kept for the line under the title, so a game whose line
   *  changes while it plays doesn't move its pieces. */
  protected leadMinLines(): number {
    return 1;
  }
  /** The line under the amount: where it went, by default. */
  protected noteText(): string {
    return destination(this.game.toPot, this.game.prize);
  }
  /** The counters showing, as "Label value" (browser tests), or null. */
  protected counterValues(): string[] | null {
    return null;
  }

  // ---- the game ---------------------------------------------------------

  /** Ask the server for an act (ignored while one is in flight). */
  protected act(action: string, arg?: string, piece?: number): void {
    this.explainer.hide();
    this.dispatch({ type: 'act', action, arg, piece });
  }

  /** Ask again for an act the server has already answered (step `step`):
   *  its first answer comes back, as when an answer was lost on the way. */
  protected replay(step: number, action: string, arg?: string): void {
    void this.send(step, action, arg);
  }

  /** Say what something is for, near `at` (content space): above it, or
   *  under it when `below`. */
  protected explain(text: string, at: { x: number; y: number }, below = false): void {
    this.explainer.show(text, at, this.contentWidth, below);
  }

  private dispatch(event: GameEvent<S, R>): void {
    const prev = this.game;
    const { view, effect } = reduceGame(prev, event);
    this.game = view;
    if (event.type !== 'act') this.onAnswered(prev, view);
    this.render();
    if (prev.phase !== 'over' && view.phase === 'over' && !this.settling) this.onOver();
    if (effect) void this.send(effect.step, effect.action, effect.arg);
  }

  private async send(step: number, action: string, arg?: string): Promise<void> {
    const api = this.api;
    if (!api) return;
    try {
      const result = (await api.act(step, action, arg)) as AnyPlayActResult<R, S>;
      this.dispatch({ type: 'answered', result });
    } catch {
      const play = (await api.recover().catch(() => null)) as AnyPlayView<O['kind'], unknown, S> | null;
      this.dispatch({ type: 'failed', play });
    }
  }

  /** Show an ended game held back by {@link settling}. */
  protected reveal(): void {
    this.settling = false;
    this.render();
    if (this.game.phase === 'over') this.onOver();
  }

  /** The game ended: what it paid sounds out (a kind, quiet end for
   *  nothing). */
  private onOver(): void {
    const prize = this.game.prize;
    const paid = !!prize && (prize.coins > 0 || prize.freeSpins > 0 || prize.gems > 0);
    const won = paid && this.wonPrize();
    const top = !!prize && (prize.gold || prize.coins >= (this.offer?.maxValue ?? Infinity));
    const sound = !won ? ROAD_SOUNDS.hiloEnd : top ? GAME_SOUNDS.topPrize : GAME_SOUNDS.coins;
    window.setTimeout(() => AudioBus.shared.play(sound, { volume: 0.8 }), 300);
    // A win gets confetti from its amount; a loss never does, and nor does
    // a visitor's thank-you for a game that didn't win (its floor).
    if (won && !prize?.floored) void this.celebrate({ x: this.amount.x, y: this.amount.y + this.amount.height / 2 });
  }

  private onSide(): void {
    if (this.game.phase === 'play') this.onSideTap();
  }

  private async onMain(): Promise<void> {
    const prize = this.game.prize;
    if (this.game.phase === 'play' && this.actionLabel()) {
      this.onActionTap();
      return;
    }
    if (this.game.phase !== 'over' || this.settling || this.closing) return;
    this.closing = true;
    this.explainer.hide();
    this.main.setEnabled(false);
    if (prize && !this.game.toPot && prize.coins > 0) await this.deps.flyCoins(this.amount.getGlobalPosition(), prize.coins);
    Ticker.shared.remove(this.tick);
    await this.close();
    this.main.setEnabled(true);
    const done = this.done;
    this.done = null;
    done?.({ prize, pot: this.game.pot });
  }

  /** Lay the board out for where the game is. */
  protected render(): void {
    if (!this.offer) return;
    const w = this.contentWidth;
    this.laidOutWidth = w;
    this.laidOutHeight = this.screenH;
    let y = 0;
    this.lead.style.wordWrapWidth = w;
    this.lead.text = this.fitLead(this.leadText(), w);
    const over = this.game.phase === 'over' && !this.settling;
    const paid = over && !!this.game.prize && prizeWords(this.game.prize) !== '';
    const won = paid && this.wonPrize();
    this.lead.style.fill = over && !won ? COLOURS.loss : COLOURS.cream;
    this.lead.position.set(w / 2, y);
    y += Math.max(this.lead.height, this.leadHeight(this.leadMinLines())) + 10;

    // The board stays clear of the top bar: the pieces get what's left of
    // the screen once the words and buttons under them are counted.
    const buttons = over ? 56 : this.sideLabel() || this.actionLabel() || this.keepButtonRoom() ? 52 : 0;
    const below = 8 + (paid ? 74 : 0) + buttons + (this.game.error ? 30 : 0);
    const budget = Math.max(150, this.screenH - BOARD_CLEARANCE - y - below);
    y = this.renderPieces(y, w, budget) + 8;

    this.amount.visible = this.note.visible = paid;
    if (paid && this.game.prize) {
      this.amount.text = prizeWords(this.game.prize);
      // A loss that pays a little shows it plainly, not as a win.
      this.amount.style = won ? displayStyle(this.game.prize.gold ? 34 : 30) : uiStyle(20, COLOURS.cream, '600');
      this.amount.position.set(w / 2, y);
      y += this.amount.height + 2;
      this.note.text = this.noteText();
      this.note.position.set(w / 2, y);
      y += this.note.height + 10;
    }

    this.error.visible = !!this.game.error;
    if (this.game.error) {
      this.error.style.wordWrapWidth = w;
      this.error.text = this.game.error;
      this.error.position.set(w / 2, y);
      y += this.error.height + 8;
    }

    // Buttons: cancel or the second choice on the left, the action on the right.
    const side = !over ? this.sideLabel() : null;
    const action = !over ? this.actionLabel() : null;
    this.side.visible = !!side;
    this.main.visible = over || !!action;
    if (over) {
      this.main.setLabel(won ? 'COLLECT' : 'DONE');
      this.main.setEnabled(true);
      layoutButtonRow(null, this.main, 0, y, w);
      y += this.main.buttonHeight + 4;
    } else if (side || action) {
      if (side) {
        this.side.setLabel(side);
        this.side.setEnabled(this.game.phase === 'play');
      }
      if (action) {
        this.main.setLabel(action);
        this.main.setEnabled(this.game.phase === 'play' && !this.settling);
      }
      if (side && action) layoutButtonRow(this.side, this.main, 0, y, w);
      else layoutButtonRow(null, action ? this.main : this.side, 0, y, w);
      y += 52;
    } else if (this.keepButtonRoom()) {
      y += 52;
    }
    this.setContentHeight(y);
  }

  /** How tall `lines` lines of the line under the title stand. */
  private leadHeight(lines: number): number {
    if (lines <= 1) return 0;
    let h = this.leadHeights.get(lines);
    if (h === undefined) {
      // Measured on a copy of the style, so the line itself is untouched.
      const probe = new Text({ text: Array(lines).fill('A').join('\n'), style: this.lead.style.clone() });
      h = probe.height;
      probe.destroy(true);
      this.leadHeights.set(lines, h);
    }
    return h;
  }
  private readonly leadHeights = new Map<number, number>();

  /** The line under the title on one line when it fits; otherwise broken
   *  between its sentences, so no word is left alone on a line. */
  private fitLead(text: string, width: number): string {
    this.lead.style.wordWrap = false;
    this.lead.text = text;
    const fits = this.lead.width <= width;
    this.lead.style.wordWrap = true;
    return fits ? text : text.replace(/([.!?]) (?=\S)/g, '$1\n');
  }
}
